/**
 * Cost Chart Component
 * Displays cost summary stats and cost over time for free tier users
 */

import React from 'react';
import { DollarSign, TrendingUp, Cpu, FileText } from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { CostData } from '../types';

interface CostChartProps {
  costData: CostData;
  granularity: string;
}

const BRAND_PRIMARY = '#03e8d3';

export const CostChart: React.FC<CostChartProps> = ({ costData, granularity }) => {
  const formatCost = (value: number): string => {
    if (value >= 1) {
      return `$${value.toFixed(2)}`;
    }
    return `$${value.toFixed(4)}`;
  };

  const formatNumber = (value: number): string => {
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M`;
    }
    if (value >= 1000) {
      return `${(value / 1000).toFixed(1)}K`;
    }
    return value.toLocaleString();
  };

  const formatPeriod = (period: string): string => {
    const date = new Date(period);
    if (granularity === 'hour') {
      return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    }
    if (granularity === 'day') {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
    if (granularity === 'week') {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
    return date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
  };

  const chartData = costData.cost_over_time.map((item) => ({
    period: formatPeriod(item.period),
    cost: item.cost,
    requests: item.requests,
  }));

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <DollarSign className="w-5 h-5" style={{ color: BRAND_PRIMARY }} />
            <h3 className="text-lg font-semibold text-gray-900">Cost Analysis</h3>
          </div>
          <span className="text-2xl font-bold" style={{ color: BRAND_PRIMARY }}>
            {formatCost(costData.total_cost)}
          </span>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-3 gap-4 p-4 bg-gray-50 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <div className="p-2 rounded-lg bg-blue-100">
            <FileText className="w-4 h-4 text-blue-600" />
          </div>
          <div>
            <p className="text-xs text-gray-500">Requests</p>
            <p className="font-semibold text-gray-900">{formatNumber(costData.total_requests)}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="p-2 rounded-lg bg-purple-100">
            <TrendingUp className="w-4 h-4 text-purple-600" />
          </div>
          <div>
            <p className="text-xs text-gray-500">Input Tokens</p>
            <p className="font-semibold text-gray-900">
              {formatNumber(costData.total_input_tokens)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="p-2 rounded-lg bg-emerald-100">
            <Cpu className="w-4 h-4 text-emerald-600" />
          </div>
          <div>
            <p className="text-xs text-gray-500">Output Tokens</p>
            <p className="font-semibold text-gray-900">
              {formatNumber(costData.total_output_tokens)}
            </p>
          </div>
        </div>
      </div>

      {/* Cost Over Time Chart */}
      <div className="p-4">
        <h4 className="text-sm font-medium text-gray-700 mb-3">Cost Over Time</h4>
        {chartData.length > 0 ? (
          <div className="h-[180px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis
                  dataKey="period"
                  tick={{ fontSize: 11, fill: '#6B7280' }}
                  tickLine={false}
                  axisLine={{ stroke: '#E5E7EB' }}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: '#6B7280' }}
                  tickLine={false}
                  axisLine={{ stroke: '#E5E7EB' }}
                  tickFormatter={(value) => formatCost(value)}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
                  }}
                  formatter={(value: number) => [formatCost(value), 'Cost']}
                />
                <Area
                  type="monotone"
                  dataKey="cost"
                  stroke={BRAND_PRIMARY}
                  fill={`${BRAND_PRIMARY}20`}
                  strokeWidth={2}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="h-[180px] flex items-center justify-center text-gray-500">
            No cost data available for this period
          </div>
        )}
      </div>
    </div>
  );
};

export default CostChart;
